import type { DrawerSection } from '../../lib/health-status.js';
import { useSoundEnabled } from '../../lib/sound.js';
import { BrandLogo } from '../BrandLogo.js';
import { SoundButton } from '../SoundButton.js';
import type { UnifiedView } from '../UnifiedDashboard.js';
import { AvatarMenu } from './AvatarMenu.js';
import { StatusCluster } from './StatusCluster.js';

/**
 * R8 — the global topbar (row 1 of the shell grid). A single 56px bar that
 * spans the full width above the rail + workspace. It carries, left→right:
 *   - the brand,
 *   - the global search (URL-backed via `?q`; see UnifiedDashboard) — shown only
 *     where it actually filters something. In R8 that is the inbox; each queue
 *     view wires its own `?q` consumption as it is ported (R14+), and the bar
 *     reveals the search there then. Showing a dead search box would be exactly
 *     the kind of decoy the R1 honesty pass removed.
 *   - the today's-spend pill + sound + theme controls (reused leaves), and
 *   - the avatar menu (Fullscreen · Keyboard shortcuts · Sign out, decision #7).
 *
 * The status/send/cost CLUSTER + alert dock are R9; this bar is built so they
 * drop into the spacer/right region without restructuring it.
 */
export function AppTopbar({
  currentView,
  query,
  onQueryChange,
  onOpenShortcuts,
  onOpenDrawer,
  onOpenPalette,
}: {
  currentView: UnifiedView;
  query: string;
  onQueryChange: (next: string) => void;
  onOpenShortcuts: () => void;
  onOpenDrawer: (section?: DrawerSection) => void;
  /** A2 — open the ⌘K command palette from a visible affordance (the palette + the
   *  whole g-key layer were powerful but undiscoverable — nothing advertised them). */
  onOpenPalette: () => void;
}) {
  const soundOn = useSoundEnabled();
  const showSearch = currentView === 'unified';

  return (
    <header className="app-topbar" data-testid="app-topbar">
      <div className="app-brand" title="Comms">
        <BrandLogo tone="dark" variant="icon" className="h-6 w-6" />
        <span>Comms</span>
      </div>

      {showSearch ? (
        <label className="app-gsearch">
          <SearchGlyph />
          <input
            type="search"
            data-testid="global-search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search people or threads"
            aria-label="Search the inbox"
          />
        </label>
      ) : null}

      <span className="app-topbar-spacer" />

      <button
        type="button"
        className="app-kbd-hint"
        data-testid="cmdk-hint"
        onClick={onOpenPalette}
        title="Jump to anything (⌘K)"
        aria-label="Open the command palette (Command-K)"
      >
        <kbd>⌘K</kbd>
        <span className="app-kbd-text">Jump</span>
      </button>

      <StatusCluster onOpenDrawer={onOpenDrawer} />
      <SoundButton enabled={soundOn} />
      <AvatarMenu onOpenShortcuts={onOpenShortcuts} />
    </header>
  );
}

function SearchGlyph() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}
